import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { CheckCircle, Circle, FileArrowUp, DownloadSimple } from "@phosphor-icons/react";
import { TrustPortalLayout } from "../components/shared/TrustPortalLayout";
import { Button } from "../components/ui/button";
import { api, API, formatApiErrorDetail } from "../lib/api";

const STAGE_LABELS = {
  "In Progress": { color: "bg-igc-muted/15 text-igc-muted border-igc-muted/30", desc: "Your intake is in progress." },
  "Intake Complete": { color: "bg-igc-gold/15 text-igc-goldText border-igc-gold/40", desc: "Intake submitted — upload any supporting documents next." },
  "Awaiting Review": { color: "bg-blue-500/15 text-blue-700 border-blue-500/40", desc: "Wes is personally reviewing your file. He'll call you before anything is finalized." },
  "Trust Delivered": { color: "bg-igc-success/15 text-igc-success border-igc-success/30", desc: "Your trust has been delivered. Congratulations!" },
};

export default function TrustPortalDashboardPage() {
  const [status, setStatus] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchStatus = async () => {
    try {
      const { data } = await api.get("/trust-portal/status");
      setStatus(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await api.post("/trust-portal/documents", formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      fetchStatus();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  if (!status) return null;
  const stageInfo = STAGE_LABELS[status.trust_stage] || STAGE_LABELS["In Progress"];
  const intake = status.intake || {};
  const allSectionsComplete = Object.values(intake.sections || {}).every((s) => s.completed);

  return (
    <TrustPortalLayout>
      <div data-testid="trust-portal-dashboard-page">
        <h1 className="font-serif text-3xl text-igc-cream tracking-tight mb-1">Welcome, {status.display_name}</h1>
        <p className="text-igc-muted text-sm mb-6">{status.email}</p>

        <div className={`inline-flex items-center gap-2 px-4 py-2 border text-sm mb-2 ${stageInfo.color}`} data-testid="trust-portal-stage-badge" title="Where your trust is at in the process, from intake through delivery.">
          {status.trust_stage}
        </div>
        <p className="text-igc-muted text-sm mb-8" data-testid="trust-portal-stage-desc">{stageInfo.desc}</p>

        {!intake.submitted ? (
          <div className="bg-igc-surface border border-igc-gold/15 p-6 mb-8" data-testid="trust-portal-intake-cta">
            <p className="text-igc-cream text-sm mb-3">{allSectionsComplete ? "All sections complete — ready to submit." : "Complete your intake to move your trust forward."}</p>
            <Link to="/trust-portal/intake"><Button data-testid="trust-portal-continue-intake-button" title="Continues your intake questionnaire from where you left off." className="bg-igc-gold hover:bg-igc-goldHover text-black">Continue Intake</Button></Link>
          </div>
        ) : (
          <div className="bg-igc-surface border border-igc-success/20 p-6 mb-8 flex items-center gap-2" data-testid="trust-portal-intake-done">
            <CheckCircle size={18} className="text-igc-success" weight="fill" /> <p className="text-igc-cream text-sm">Intake submitted.</p>
          </div>
        )}

        <h2 className="font-serif text-xl text-igc-cream tracking-tight mb-4" title="Upload any supporting paperwork IGC needs to finalize your trust — IDs, deeds, statements, etc.">Documents</h2>
        <div className="bg-igc-surface border border-igc-gold/15 p-5 mb-4">
          <label className="flex items-center gap-2 text-igc-goldText text-sm cursor-pointer" data-testid="trust-portal-upload-label" title="Click to choose a file from your device and upload it to your trust file.">
            <FileArrowUp size={18} weight="duotone" />
            {uploading ? "Uploading..." : "Upload a document"}
            <input ref={fileInputRef} data-testid="trust-portal-upload-input" type="file" className="hidden" disabled={uploading} onChange={handleUpload} />
          </label>
        </div>
        {status.documents.length === 0 ? (
          <p className="text-igc-muted text-sm" data-testid="trust-portal-documents-empty">No documents uploaded yet.</p>
        ) : (
          <div className="space-y-2">
            {status.documents.map((d) => (
              <a key={d.id} href={`${API}/trust-portal/documents/${d.id}/download`} target="_blank" rel="noreferrer" data-testid={`trust-portal-document-${d.id}`} title="Opens or downloads this document." className="flex items-center justify-between bg-igc-surface border border-igc-gold/15 p-4 text-igc-cream text-sm hover:border-igc-gold/40 transition-colors">
                {d.original_filename}
                <DownloadSimple size={16} className="text-igc-goldText" />
              </a>
            ))}
          </div>
        )}
      </div>
    </TrustPortalLayout>
  );
}
